import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Slider,
    Stack,
    Box,
    Typography,
    CircularProgress,
} from '@mui/material';
import useResponsive from '../../../hooks/minimal/useResponsive.jsx';
import { useLocales } from '../../../locales';
import { useSnackbar } from '../snackbar/index.jsx';
import {
    frameScale,
    clampPan,
    clampZoom,
    loadOrientedSource,
    bakeAvatarFile,
} from '../../../utils/avatarFraming';

// ----------------------------------------------------------------------

const AvatarPositionEditor = ({ open, file, onCancel, onConfirm }) => {
    const isMobile = useResponsive('down', 'sm');
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();

    const V = isMobile ? 260 : 300;
    // CSS size stays V; the canvas backing store is V*dpr for crisp high-DPI output.
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const backing = Math.round(V * dpr);

    const canvasRef = useRef(null);
    // Loaded, orientation-corrected image source: { source, width, height }.
    const imgRef = useRef(null);
    // Active pointers for drag / pinch. Map<pointerId, {x, y}>.
    const pointersRef = useRef(new Map());
    // Pinch baseline captured when the second pointer goes down.
    const pinchRef = useRef(null);
    // Set when dropping from 2 -> 1 pointer so the next move re-seeds (no jump).
    const reseedRef = useRef(false);

    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [zoom, setZoom] = useState(1);
    // Mirror of `zoom` for use in pointer handlers that close over stale state.
    const zoomRef = useRef(1);
    // RAW (unclamped) pan accumulator; clamped on render and written back on pointer-up.
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);

    const titleId = 'avatar-position-editor-title';

    // Keep zoomRef in sync so pointer handlers read the live zoom, not a stale closure.
    useEffect(() => {
        zoomRef.current = zoom;
    }, [zoom]);

    // Draw the current frame. Mirrors bakeAvatarFile's geometry so preview === bake.
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img) return;
        const { source, width, height } = img;
        const s = frameScale(width, height, V, zoom);
        const { tx, ty } = clampPan(panX, panY, width, height, V, zoom);
        const ctx = canvas.getContext('2d');
        // High-DPI: backing store is V*dpr, but all geometry below stays in CSS px.
        const dpr = window.devicePixelRatio || 1;
        const backing = Math.round(V * dpr);
        if (canvas.width !== backing) canvas.width = backing;
        if (canvas.height !== backing) canvas.height = backing;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // map CSS px -> backing px
        ctx.clearRect(0, 0, V, V);
        ctx.save();
        ctx.translate(V / 2 + tx, V / 2 + ty);
        ctx.scale(s, s);
        ctx.drawImage(source, -width / 2, -height / 2);
        ctx.restore();
    }, [V, zoom, panX, panY]);

    // Load the image whenever the dialog opens with a file.
    useEffect(() => {
        if (!open || !file) return undefined;
        let cancelled = false;
        setReady(false);
        (async () => {
            try {
                const loaded = await loadOrientedSource(file);
                if (cancelled) {
                    loaded.source?.close?.();
                    return;
                }
                imgRef.current = loaded;
                setZoom(1);
                setPanX(0);
                setPanY(0);
                setReady(true);
            } catch {
                if (!cancelled) {
                    enqueueSnackbar(translate('general_error'), { variant: 'error' });
                }
            }
        })();
        return () => {
            cancelled = true;
            imgRef.current?.source?.close?.();
            imgRef.current = null;
            pointersRef.current.clear();
            pinchRef.current = null;
        };
    }, [open, file, enqueueSnackbar, translate]);

    // Redraw on any geometry change once the image is ready.
    useEffect(() => {
        if (ready) draw();
    }, [ready, draw]);

    // --- Pointer handling (mouse + touch via Pointer Events) ---------------

    const onPointerDown = (e) => {
        if (!ready) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 2) {
            const [a, b] = [...pointersRef.current.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            pinchRef.current = { dist: dist || 1, zoom };
        }
    };

    const onPointerMove = (e) => {
        if (!ready) return;
        const prev = pointersRef.current.get(e.pointerId);
        if (!prev) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointersRef.current.size >= 2 && pinchRef.current) {
            // Pinch zoom: ratio of current finger distance vs. baseline.
            const [a, b] = [...pointersRef.current.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            const ratio = dist / pinchRef.current.dist;
            setZoom(clampZoom(pinchRef.current.zoom * ratio));
            return;
        }

        // Just dropped from a pinch to one finger: stored position is stale, so
        // re-seed it this frame and skip applying a delta (avoids a pan jump).
        if (reseedRef.current) {
            reseedRef.current = false;
            return;
        }

        // Single-pointer drag: accumulate raw pan (clamped on render).
        const dx = e.clientX - prev.x;
        const dy = e.clientY - prev.y;
        setPanX((px) => px + dx);
        setPanY((py) => py + dy);
    };

    const endPointer = (e) => {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) {
            pinchRef.current = null;
        }
        // Dropped from a pinch to exactly one finger: the survivor's stored
        // position is stale, so re-seed it on the next move (no spurious drag).
        if (pointersRef.current.size === 1) {
            reseedRef.current = true;
        }
        // Write the clamped pan back so the raw accumulator can't drift.
        // Use zoomRef (live) rather than the closure zoom, which may be stale
        // right after a pinch updated zoom in the same gesture.
        const img = imgRef.current;
        if (img) {
            const { width, height } = img;
            const clamped = clampPan(panX, panY, width, height, V, zoomRef.current);
            setPanX(clamped.tx);
            setPanY(clamped.ty);
        }
    };

    const handleZoomChange = (_e, value) => {
        setZoom(clampZoom(Array.isArray(value) ? value[0] : value));
    };

    const handleSave = async () => {
        const img = imgRef.current;
        if (!img || saving) return;
        const { source, width, height } = img;
        const clamped = clampPan(panX, panY, width, height, V, zoom);
        setSaving(true);
        try {
            const framed = await bakeAvatarFile(source, width, height, V, {
                zoom,
                tx: clamped.tx,
                ty: clamped.ty,
            });
            await onConfirm(framed);
        } catch {
            enqueueSnackbar(translate('general_error'), { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // --- Shared pieces ------------------------------------------------------

    const viewport = (
        <Box
            role="group"
            aria-roledescription={translate('avatar_editor.reposition_aria')}
            aria-label={translate('avatar_editor.reposition_aria')}
            sx={{
                width: V,
                height: V,
                borderRadius: '50%',
                overflow: 'hidden',
                touchAction: 'none',
                cursor: 'grab',
                bgcolor: 'background.neutral',
                mx: 'auto',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
        >
            <canvas
                ref={canvasRef}
                width={backing}
                height={backing}
                style={{ width: V, height: V, display: 'block', touchAction: 'none' }}
            />
        </Box>
    );

    const zoomSlider = (
        <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.01}
            onChange={handleZoomChange}
            disabled={!ready || saving}
            aria-label={translate('avatar_editor.zoom_aria')}
        />
    );

    // --- Render -------------------------------------------------------------

    return (
        <Dialog
            open={open}
            onClose={() => { if (!saving) onCancel(); }}
            fullScreen={isMobile}
            maxWidth="xs"
            fullWidth
            aria-labelledby={titleId}
        >
            {isMobile ? (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.5 }}
                    >
                        <Button onClick={onCancel} disabled={saving} color="inherit">
                            {translate('avatar_editor.cancel')}
                        </Button>
                        <Typography id={titleId} variant="subtitle1">
                            {translate('avatar_editor.title')}
                        </Typography>
                        <Box sx={{ width: 64 }} />
                    </Stack>

                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        {viewport}
                        <Box sx={{ width: '100%', px: 1 }}>{zoomSlider}</Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 2, pb: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleSave}
                            disabled={!ready || saving}
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            {translate('avatar_editor.save')}
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle id={titleId}>{translate('avatar_editor.title')}</DialogTitle>

                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pt: 2 }}>
                        {viewport}
                        <Box sx={{ width: '100%', px: 1 }}>{zoomSlider}</Box>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button variant="outlined" onClick={onCancel} disabled={saving}>
                            {translate('avatar_editor.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!ready || saving}
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            {translate('avatar_editor.save')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default AvatarPositionEditor;
